import React from "react";
import { 
  Ruler, RotateCcw, Map, CloudRain, Grid, Ghost
} from "lucide-react";

/**
 * 3D 뷰어 툴바
 * @param {Object} props
 * @param {string} props.activeMode - 현재 활성화된 모드
 * @param {boolean} props.isWireframe - 와이어프레임 모드 여부
 * @param {boolean} props.isGhost - 반투명(Ghost) 모드 여부
 * @param {Function} props.onModeChange - 모드 변경 핸들러
 * @param {Function} props.onToggleWireframe - 와이어프레임 토글
 * @param {Function} props.onToggleGhost - 반투명 토글
 * @param {Function} props.onReset - 뷰/상태 초기화 핸들러
 */
const ViewerToolbar = ({ 
    activeMode, isWireframe, isGhost, 
    onModeChange, onToggleWireframe, onToggleGhost, onReset 
}) => {
  const tools = [
    { id: "measure", icon: Ruler, label: "거리 측정", desc: "두 지점 클릭" },
    { id: "map", icon: Map, label: "지도 보기", desc: "바닥에 지도 표시" },
    { id: "weather", icon: CloudRain, label: "날씨 효과", desc: "비/눈 시뮬레이션" },
  ];

  return (
    <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
      <div className="bg-black/60 backdrop-blur-md border border-cyan-500/30 p-1.5 rounded-xl shadow-xl flex flex-col gap-1">
        
        {/* Visual Modes */}
        <button
            onClick={onToggleWireframe}
            className={`
              relative p-2.5 rounded-lg transition-all duration-300 group
              ${isWireframe 
                ? "bg-amber-500/20 text-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.3)] border border-amber-500/50" 
                : "text-slate-400 hover:bg-white/10 hover:text-white border border-transparent"}
            `}
            title="와이어프레임"
        >
            <Grid size={20} />
            <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 px-2 py-1 bg-black/80 border border-gray-700 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="font-bold text-amber-400 block mb-0.5">와이어프레임</span>
              <span className="text-gray-400">구조선 보기</span>
            </div>
        </button>

        <button
            onClick={onToggleGhost}
            className={`
              relative p-2.5 rounded-lg transition-all duration-300 group
              ${isGhost 
                ? "bg-purple-500/20 text-purple-400 shadow-[0_0_10px_rgba(192,132,252,0.3)] border border-purple-500/50" 
                : "text-slate-400 hover:bg-white/10 hover:text-white border border-transparent"}
            `}
            title="반투명"
        >
            <Ghost size={20} />
            <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 px-2 py-1 bg-black/80 border border-gray-700 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="font-bold text-purple-400 block mb-0.5">반투명 모드</span>
              <span className="text-gray-400">내부 구조 보기</span>
            </div>
        </button>

        <div className="h-px bg-gray-700/50 my-1 mx-2" />

        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => onModeChange(activeMode === tool.id ? "none" : tool.id)}
            className={`
              relative p-2.5 rounded-lg transition-all duration-300 group
              ${activeMode === tool.id 
                ? "bg-cyan-500/20 text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.3)] border border-cyan-500/50" 
                : "text-slate-400 hover:bg-white/10 hover:text-white border border-transparent"}
            `}
            title={tool.label}
          >
            <tool.icon size={20} />
            
            {/* Tooltip (Left side) */}
            <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 px-2 py-1 bg-black/80 border border-gray-700 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="font-bold text-cyan-400 block mb-0.5">{tool.label}</span>
              <span className="text-gray-400">{tool.desc}</span>
            </div>
          </button>
        ))}

        <div className="h-px bg-gray-700/50 my-1 mx-2" />

        <button
            onClick={onReset}
            className="p-2.5 rounded-lg text-slate-400 hover:bg-red-500/20 hover:text-red-400 transition-all border border-transparent hover:border-red-500/30 group relative"
            title="초기화"
        >
            <RotateCcw size={20} />
             <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 px-2 py-1 bg-black/80 border border-gray-700 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="font-bold text-red-400">뷰 초기화</span>
            </div>
        </button>
      </div>
    </div>
  );
};

export default ViewerToolbar;
